import { useEffect, useState } from 'preact/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { route } from 'preact-router';

import { authRepo } from '@/data/repositories/auth-repo';
import { tokenStore } from '@/data/storage/token-store';
import { config } from '@/config';
import type { AuthResponse } from '@/domain/types';

const authQueryKey = ['auth', 'info'] as const;

export function useAuthInfo() {
  return useQuery({
    queryKey: authQueryKey,
    queryFn: authRepo.info,
    staleTime: 60_000,
  });
}

export function useIsAuthenticated() {
  const [token, setToken] = useState<string | null>(() => tokenStore.get());
  useEffect(() => tokenStore.subscribe(t => setToken(t)), []);
  return !!token;
}

function applyAuth(qc: ReturnType<typeof useQueryClient>, r: AuthResponse) {
  tokenStore.set(r.token ?? null);
  qc.invalidateQueries({ queryKey: authQueryKey });
  route(config.routes.home);
}

export function useEmailLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authRepo.loginEmail(email, password),
    onSuccess: (r) => applyAuth(qc, r),
  });
}

export function useEmailRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { email: string; password: string; displayName?: string; refCode?: number }) =>
      authRepo.registerEmail(b.email, b.password, b.displayName, b.refCode),
    onSuccess: (r) => applyAuth(qc, r),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: ({ email }: { email: string }) => authRepo.forgetPass(email),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, newPassword }: { token: string; newPassword: string }) =>
      authRepo.resetPass(token, newPassword),
  });
}

export function useSocialLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { provider: 'google' | 'facebook' | 'x'; token: string; refCode?: number }) =>
      p.provider === 'google' ? authRepo.google(p.token, p.refCode)
      : p.provider === 'facebook' ? authRepo.facebook(p.token, p.refCode)
      : authRepo.x(p.token, p.refCode),
    onSuccess: (r) => applyAuth(qc, r),
  });
}

export function useTelegramLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { initData: string; refCode?: number }) =>
      authRepo.telegram(p.initData, p.refCode),
    onSuccess: (r) => applyAuth(qc, r),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    // Best-effort server acknowledgement; the token is always cleared locally
    // even if the call fails (offline, expired token, etc.).
    try { await authRepo.logout(); } catch { /* ignore */ }
    tokenStore.clear();
    qc.clear();
    route(config.routes.login);
  };
}
